import { isEmpty } from 'lodash';
import { createSelector } from 'reselect';
import { getDeliveryMethod } from 'src/redux/modules/sellDeliveryMethod/sellDeliveryMethod.selectors';
import {
  COLLEGE_PLAN_TYPES,
  PRODUCT_TYPE_SORT_ENUM_MAP,
} from 'src/settings/settings';
import { UserFund } from 'src/types';
import { validateCollegeContributeFund } from 'src/utils/form/validate/validate-college';
import { updateCollegeDistributeFundNotesForFund } from 'src/utils/funds/college/college-distribute-fund-notes-utility';
import { updateCollegeFundNotesForFund } from 'src/utils/funds/college/college-fund-notes-utility';
import { updateSharedAddFundNotesForFund } from 'src/utils/funds/shared/add-fund-notes-utility';
import { setCollegeExchangeSharePrice } from 'src/utils/funds/funds-utility';
import {
  coalesce,
  hasValue,
  stringPropSorter,
  sumArray,
  toLookup,
} from 'src/utils/utils';
import { getSelectedBankAgreementMap } from '../bankSelection/bankSelection.selectors';
import { getCollegeAllFundsMap } from '../funds/AllFunds/allFunds.selectors';
import { getBuyPendingTransactions } from '../pendingTransactions/pendingTransactions.selectors';
import {
  getIsCheckWithMultipleAddress,
  getSelectedSellFundBene,
  getValidatedSellFundsList,
} from '../sellTransaction/sellTransaction.selectors';
import { getPersonalInformationData } from '../user/PersonalInformation/personalInformation.selectors';
import { UserRegistration } from '../user/UserRegistrationTypes/types';
import { getSelectedRegistrationType } from '../user/UserRegistrationTypes/UserRegistrationTypes.selectors';
import {
  getAvailableFundList,
  getNewFunds,
  getUserFundsRawList,
} from './shoppingCart.selectors';
import { isCollegeDistribute } from '../../../utils/transactions-util';

/**
 *
 * @param {UserFund} xFund
 * @param {UserFund} yFund
 * @returns {number}
 */
function collegeFundSorter(xFund: UserFund, yFund: UserFund) {
  const xNames = coalesce(xFund.formattedEntityNames, '');
  const yNames = coalesce(yFund.formattedEntityNames, '');

  const diffNames = xNames.localeCompare(yNames);
  if (diffNames) {
    return diffNames;
  }

  const xType = coalesce(
    PRODUCT_TYPE_SORT_ENUM_MAP[xFund.productType],
    Number.MAX_VALUE
  );
  const yType = coalesce(
    PRODUCT_TYPE_SORT_ENUM_MAP[yFund.productType],
    Number.MAX_VALUE
  );

  const diffType = xType - yType;
  if (diffType) {
    return diffType;
  }

  const xFundName = coalesce(xFund.fundName, '');
  const yFundName = coalesce(yFund.fundName, '');
  return xFundName.localeCompare(yFundName);
}

/**
 * @typedef {Object.<string, Object.<string, number>>} GroupedShoppingCartMax
 *Example:
 {
    "Betty L. Barker, Andrew L. Barker": {
      "Maryland529": 190287.98
    },
    "Betty L. Barker, Charle J. Barker": {
      "Maryland529": 118131.96
    }
  }
 */

/**
 * Returns sum of total funds that count towards plan max for each plan
 * This consists of available balance, pending transactions, and shopping cart selections
 * @param state
 * @returns {GroupedShoppingCartMax}
 */
export const getCollegeFundsTowardsPlanMax = createSelector(
  [getAvailableFundList, getBuyPendingTransactions],
  (availableFunds: UserFund[], pendingTransactions) => {
    const fundsTowardsMax = {};

    availableFunds.forEach((fund) => {
      // Calculate pending transactions on same agreementID
      const pendingTransactionForAgreement = sumArray(
        pendingTransactions[fund.agreementID],
        (trans) => trans.amount,
        0
      );

      // Calculate shopping cart total on same agreementID
      const shoppingCartSelectionTotal = coalesce(
        fund.selected && fund.currentYearAmount,
        0
      );

      const validationSum =
        (fund.balanceAsOf || 0) +
        shoppingCartSelectionTotal +
        pendingTransactionForAgreement;

      if (
        fund.formattedEntityNames in fundsTowardsMax &&
        fund.productType in fundsTowardsMax[fund.formattedEntityNames]
      ) {
        fundsTowardsMax[fund.formattedEntityNames][fund.productType] +=
          validationSum;
      } else {
        fundsTowardsMax[fund.formattedEntityNames] = {
          ...fundsTowardsMax[fund.formattedEntityNames],
          [fund.productType]: validationSum,
        };
      }
    });

    return fundsTowardsMax;
  }
);

/**
 * Returns available fund list sorted by College plan priorities (beneficiary, type, portfolio name)
 * @param state
 * @returns {AvailableFund[]}
 */
export const getCollegeAvailableFundList = createSelector(
  getAvailableFundList,
  getCollegeFundsTowardsPlanMax,
  getPersonalInformationData,
  getSelectedBankAgreementMap,
  /**
   * @param {AvailableFund[]} availableFundList
   * @param {GroupedShoppingCartMax} collegeFundsTowardsPlanMax
   * @returns {*} personalInformationData
   */
  (
    availableFundList: UserFund[],
    collegeFundsTowardsPlanMax,
    personalInformationData,
    selectedBankAgreementMap
  ) => {
    const validatedFunds = availableFundList
      .map((origFund) => {
        const fund = {
          ...origFund,
          disabled: false,
          notes: {},
        };
        // Reset notes that were added by buy selector and add college specific fund notes
        updateCollegeFundNotesForFund(
          fund,
          personalInformationData,
          selectedBankAgreementMap
        );

        return validateCollegeContributeFund({
          fund,
          collegeFundsTowardsPlanMax,
        });
      })
      .filter((fund) => !fund.hiddenFromOnlineCustomer && !fund.viewOnly);
    return validatedFunds.sort(collegeFundSorter);
  }
);

export const getCollegeToNewAvailableFundList = createSelector(
  getCollegeAllFundsMap,
  getNewFunds,
  getSelectedRegistrationType,
  getUserFundsRawList,

  /**
   * Returns the list of portfolios that can be added based on the selected registration type. Since
   * the server provides a separate list for each already owned portfolio. So to get the list we
   * need to start with the selected registration type, map that to any of the matching existing
   * portfolios, and then return that portfolio's all portfolio list.
   *
   * @param {Object.<string, AllFund[]>} collegeAllFundsMap
   * @param {UserFund[]} newFunds
   * @param {UserRegistration} selectedRegistrationType
   * @param {UserFund[]} userFundsRawList
   * @returns {AllFund[]}
   */
  (
    collegeAllFundsMap: Record<string, UserFund[]>,
    newFunds: UserFund[],
    selectedRegistrationType: UserRegistration[] | UserRegistration,
    userFundsRawList: UserFund[]
  ) => {
    if (isEmpty(selectedRegistrationType) || isEmpty(collegeAllFundsMap)) {
      return [];
    }

    const modelAgreementUniqueIDs = Array.isArray(selectedRegistrationType)
      ? selectedRegistrationType.reduce(
          (ids, registration) =>
            ids.concat(registration.modelAgreementUniqueIDs),
          [] as string[]
        )
      : selectedRegistrationType.modelAgreementUniqueIDs;

    if (isEmpty(modelAgreementUniqueIDs)) {
      return [];
    }

    const modelAgreementUniqueID = modelAgreementUniqueIDs.find((id) =>
      hasValue(collegeAllFundsMap[id])
    );
    if (!modelAgreementUniqueID) {
      return [];
    }

    const userFundByUniqueKey = toLookup(
      userFundsRawList,
      (fund) => fund.uniqueKey
    );

    const updatedRegistrationType = {
      ...selectedRegistrationType,
      existingTickers: modelAgreementUniqueIDs.map(
        (uniqueID) => (userFundByUniqueKey[uniqueID] || {}).fundName
      ),
    };

    const allFunds = collegeAllFundsMap[modelAgreementUniqueID];
    return allFunds
      .map((fund) => {
        const newFund = {
          ...fund,
          ticker: fund.name,
          notes: {},
        };
        updateSharedAddFundNotesForFund(
          newFund,
          newFunds,
          updatedRegistrationType
        );
        return newFund;
      })
      .sort(stringPropSorter((fund) => fund.name));
  }
);

/**
 * Gets list of selected 529 funds
 * @param state
 * @returns {AvailableFund[]}
 */
export const getCollegeSelectedFunds = createSelector(
  getCollegeAvailableFundList,
  /**
   * @param {AvailableFund[]} collegeAvailableFundList
   * @returns {AvailableFund[]}
   */
  (collegeAvailableFundList) =>
    collegeAvailableFundList.filter((fund) => fund.selected)
);

/**
 * Determines if any 529 selected funds have an error
 * @param state
 * @returns {boolean} indicates if an error exists
 */
export const getCollegeSelectedFundsError = createSelector(
  getCollegeSelectedFunds,
  /**
   * @param {AvailableFund[]} collegeSelectedFunds
   * @returns {boolean}
   */
  (collegeSelectedFunds) =>
    collegeSelectedFunds.length === 0 ||
    collegeSelectedFunds.some(
      ({ collegePlanMaxError, currentYearError }) =>
        currentYearError || collegePlanMaxError
    )
);

/**
 * Determines which college contribute confirmation page to show
 * @param state
 * @returns {string}
 */
export const getCollegeConfirmType = createSelector(
  getCollegeAvailableFundList,
  (collegeAvailableFundList) => {
    const unique = [
      /** @ts-expect-error downlevel iterator issues */
      ...new Set(collegeAvailableFundList.map((e) => e.productType)),
    ];
    return unique.length > 1
      ? unique.includes(COLLEGE_PLAN_TYPES.NATIONAL_529)
        ? COLLEGE_PLAN_TYPES.NATIONAL_529
        : COLLEGE_PLAN_TYPES.MARYLAND_529
      : unique[0];
  }
);

/**
 * Adds fund notes to college distribute funds list
 * @param state
 * @returns {AvailableFund[]}
 */
export const getCollegeSellFundsList = createSelector(
  getCollegeAllFundsMap,
  getDeliveryMethod,
  getIsCheckWithMultipleAddress,
  getSelectedBankAgreementMap,
  getValidatedSellFundsList,
  getSelectedSellFundBene,
  (
    collegeAllFundsMap,
    deliveryMethod,
    isCheckWithMultipleAddress: boolean,
    selectedBankAgreementMap,
    validatedSellFundsList: UserFund[],
    selectedBene
  ) =>
    validatedSellFundsList
      .map((origFund) => {
        const fund = {
          ...origFund,
          notes: {},
        };
        if (isCollegeDistribute()) {
          setCollegeExchangeSharePrice(fund, collegeAllFundsMap);
        }
        updateCollegeDistributeFundNotesForFund(
          deliveryMethod,
          fund,
          isCheckWithMultipleAddress,
          selectedBankAgreementMap,
          selectedBene
        );
        return fund;
      })
      .sort(collegeFundSorter)
);
